import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@marketplace/ui/notifications";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  usePostGeneratePresignedUrlMutation,
  usePostUploadMediaMutation
} from "@/hooks";

import type { BankAccountData } from "../../../settings/dtos/bank-account-dto";
import { claimOrderSubmitModalAtom } from "../../atoms/claim-order-submit-modal-atom";
import type { OrderClaimOrderItem } from "../../dtos/submit-claim-order-dto";
import { useDeleteClaimMutation } from "../mutations/use-delete-claim-mutation";
import { usePostSubmitClaimOrderMutation } from "../mutations/use-post-submit-claim-order-mutation";
import { useGetClaimEstimationQuery } from "../queries/use-get-claim-estimation-query";
import {
  type ClaimOrderSubmitFormSchema,
  useClaimOrderSubmitFormSchema
} from "../schemas/use-claim-order-submit-form-schema";

type UseClaimOrderSubmitFormProps = {
  onSuccess?: () => void;
  bankAccountData?: BankAccountData[];
};

const DEFAULT_VALUES = {
  invoiceNumber: "",
  invoiceFile: null,
  invoiceAmount: "",
  invoiceDate: undefined as Date | undefined,
  taxInvoiceNumber: "",
  bankAccountId: null
};

export const useClaimOrderSubmitForm = ({
  onSuccess,
  bankAccountData
}: UseClaimOrderSubmitFormProps = {}) => {
  const { t } = useTranslation("order", {
    keyPrefix: "claimOrderPage.submitModal"
  });
  const [modal] = useAtom(claimOrderSubmitModalAtom);
  const resetModal = useResetAtom(claimOrderSubmitModalAtom);
  const schema = useClaimOrderSubmitFormSchema();
  const { mutateAsync, isPending } = usePostSubmitClaimOrderMutation();
  const { mutateAsync: mutateDeleteClaimAsync } = useDeleteClaimMutation();
  const uploadMediaMutation = usePostUploadMediaMutation();
  const { mutateAsync: mutateGeneratePresignedUrlAsync } =
    usePostGeneratePresignedUrlMutation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isPresignedLoading, setIsPresignedLoading] = useState(false);
  const [tempUploadedInvoicePath, setTempUploadedInvoicePath] = useState<
    string | null
  >(null);

  const orderIds = modal.data?.orders?.map((o) => o.order_id) ?? [];
  const { data: estimation, isLoading: isEstimationLoading } =
    useGetClaimEstimationQuery({ orderIds });

  const formMethods = useForm<ClaimOrderSubmitFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
    mode: "onChange"
  });

  const { handleSubmit, reset, getValues, setValue } = formMethods;

  useEffect(() => {
    const prefill = modal.prefill;
    if (modal.visible && prefill && bankAccountData) {
      const matchingBank = bankAccountData.find(
        (b) =>
          b.bank_name === prefill.bankName &&
          b.account_number === prefill.accountNumber &&
          b.account_name === prefill.accountName
      );

      reset({
        invoiceNumber: prefill.invoiceNumber,
        invoiceFile: prefill.invoiceFilePath ?? null,
        invoiceAmount: prefill.invoiceAmount,
        invoiceDate: prefill.invoiceDate
          ? new Date(prefill.invoiceDate)
          : undefined,
        taxInvoiceNumber: prefill.taxInvoiceNumber ?? "",
        bankAccountId: matchingBank ? String(matchingBank.id) : null
      });

      setTempUploadedInvoicePath(prefill.invoiceFilePath || null);
    }
  }, [modal.visible, modal.prefill, bankAccountData]);

  useEffect(() => {
    if (!modal.visible) return;
    const claimAmount = estimation?.data?.claim_amount;
    if (claimAmount == null) return;
    setValue("invoiceAmount", claimAmount, {
      shouldValidate: true,
      shouldDirty: false
    });
  }, [modal.visible, estimation?.data?.claim_amount, setValue]);

  const resetForm = () => {
    reset(DEFAULT_VALUES);
    setShowConfirm(false);
    setTempUploadedInvoicePath(null);
  };

  const handleClose = () => {
    resetForm();
    resetModal();
  };

  const onValidSubmit: SubmitHandler<ClaimOrderSubmitFormSchema> = () => {
    setShowConfirm(true);
  };

  const handleInvoiceFileOnChange = async (file: File | null) => {
    if (!file) return;

    setValue("invoiceFile", file, { shouldValidate: true });
    setIsPresignedLoading(true);

    try {
      const response = await mutateGeneratePresignedUrlAsync({
        mime_type: file.type,
        size: file.size,
        type: "document"
      });

      if (response?.data?.media_path) {
        setTempUploadedInvoicePath(response.data.media_path);
      }
    } catch {
      notifications.show({
        variant: "error",
        message: t("presignedUrlFailed")
      });
    } finally {
      setIsPresignedLoading(false);
    }
  };

  const handleDeleteInvoiceFile = () => {
    setValue("invoiceFile", null, { shouldValidate: true });
    setTempUploadedInvoicePath(modal.prefill?.invoiceFilePath || null);
  };

  const handleConfirmSubmit = async () => {
    const values = getValues();
    const orders = modal.data?.orders ?? [];
    const isCancelledResubmit =
      modal.currentStatus === "cancelled" && !!modal.claimId;

    try {
      const invoiceFilePath = tempUploadedInvoicePath;

      if (values.invoiceFile instanceof File && tempUploadedInvoicePath) {
        uploadMediaMutation
          .mutateAsync({
            type: "document",
            object_name: "claim-order",
            object_type: "claim-order",
            file: values.invoiceFile,
            reference_no: tempUploadedInvoicePath.split("/").pop()
          })
          .catch((err) => {
            console.error("Background invoice upload failed:", err);
          });
      }

      const orderItems: OrderClaimOrderItem[] = orders.map((o) => ({
        order_id: o.order_id,
        seller_company_name: o.seller_company_name,
        buyer_company_name: o.buyer_company_name
      }));

      if (isCancelledResubmit) {
        try {
          await mutateDeleteClaimAsync({ claimId: modal.claimId! });
        } catch (err: unknown) {
          const e = err as {
            response?: { status?: number; data?: { code?: string } };
          };
          const status = e?.response?.status;
          const code = e?.response?.data?.code;
          const alreadyGone = status === 404 || code === "MRP-API-291";
          if (!alreadyGone) throw err;
        }
      }

      await mutateAsync({
        claim_id:
          !isCancelledResubmit && modal.currentStatus === "reject"
            ? modal.claimId
            : undefined,
        bank_account_id: Number(values.bankAccountId),
        invoice_number: values.invoiceNumber,
        invoice_file_path: invoiceFilePath as string,
        invoice_amount: Number(values.invoiceAmount),
        invoice_date: values.invoiceDate!.toISOString(),
        tax_invoice_number: values.taxInvoiceNumber,
        orders: orderItems
      });

      notifications.show({
        message: t("successMessage")
      });

      onSuccess?.();
      handleClose();
    } catch {
      notifications.show({
        variant: "error",
        message: t("errorMessage")
      });
    }
  };

  return {
    formMethods,
    showConfirm,
    setShowConfirm,
    isModalVisible: modal.visible,
    orders: modal.data?.orders ?? [],
    estimation: estimation?.data?.estimation_amount,
    estimationData: estimation?.data,
    isEstimationLoading,
    handleSubmit: handleSubmit(onValidSubmit),
    handleConfirmSubmit,
    handleClose,
    handleInvoiceFileOnChange,
    handleDeleteInvoiceFile,
    isPending: isPending || uploadMediaMutation.isPending || isPresignedLoading,
    isPresignedLoading
  };
};
